import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axios';
import {
  Container, Card, CardContent, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Box, Tooltip, Skeleton, Paper, Chip, Divider, useTheme, useMediaQuery, Alert, Fade
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';

export default function BranchesPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // If we support routing like /branches/edit/:id
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    code: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    defaultSafeId: '',
    defaultBankId: '',
    isActive: true,
  });

  const [branches, setBranches] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [editingId, setEditingId] = useState(null);

  // Fetch Data
  useEffect(() => {
    if (!companyId) return;

    Promise.all([
      axios.get(`/Branch/by-company/${companyId}`),
      axios.get(`/Safe/by-company/${companyId}`),
      axios.get(`/Bank/by-company/${companyId}`)
    ])
      .then(([branchesRes, safesRes, banksRes]) => {
        setBranches(branchesRes.data);
        setSafes(safesRes.data);
        setBanks(banksRes.data);
      })
      .catch(err => {
        console.error('Error fetching data:', err);
        setErrorMessage('فشل في تحميل البيانات');
      })
      .finally(() => setLoadingData(false));
  }, [companyId]);

  // Handle Edit Selection (either from URL or Click)
  useEffect(() => {
    if (id && branches.length > 0) {
       const branch = branches.find(b => b.id === parseInt(id));
       if (branch) selectBranchForEdit(branch);
    }
  }, [id, branches]);

  const selectBranchForEdit = (branch) => {
    setEditingId(branch.id);
    setFormData({
      name: branch.name,
      nameAr: branch.nameAr || '',
      code: branch.code || '',
      address: branch.address || '',
      city: branch.city || '',
      phone: branch.phone || '',
      email: branch.email || '',
      defaultSafeId: branch.defaultSafeId || '',
      defaultBankId: branch.defaultBankId || '',
      isActive: branch.isActive,
    });
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`/Branch/by-company/${companyId}`);
      setBranches(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoadingSubmit(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const payload = {
        ...formData,
        companyId,
        defaultSafeId: formData.defaultSafeId || null,
        defaultBankId: formData.defaultBankId || null,
      };

      if (editingId) {
        await axios.put(`/Branch/${editingId}`, payload);
        setSuccessMessage('✅ تم تحديث الفرع بنجاح!');
      } else {
        await axios.post(`/Branch`, payload);
        setSuccessMessage('✅ تم إضافة الفرع بنجاح!');
      }
      
      // Reset
      setFormData({
        name: '', nameAr: '', code: '', address: '', city: '', phone: '', email: '',
        defaultSafeId: '', defaultBankId: '', isActive: true
      });
      setEditingId(null);
      if(id) navigate('/branches'); // Clear URL if editing via route

      await fetchBranches();
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error(err);
      setErrorMessage(err.response?.data?.message || '❌ حدث خطأ أثناء الحفظ');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('❗ هل أنت متأكد من حذف هذا الفرع؟')) return;
    try {
      await axios.delete(`/Branch/${id}`);
      await fetchBranches();
      setSuccessMessage('✅ تم حذف الفرع بنجاح!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setErrorMessage('❌ حدث خطأ أثناء الحذف');
    }
  };

  const handleCancel = () => {
      setEditingId(null);
      setFormData({
        name: '', nameAr: '', code: '', address: '', city: '', phone: '', email: '',
        defaultSafeId: '', defaultBankId: '', isActive: true
      });
      if(id) navigate('/branches');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }} dir="rtl">
      {/* Header */}
      <Paper elevation={3} sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          borderRadius: 3, mb: 4, p: 3
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StoreIcon sx={{ fontSize: 40, color: 'white' }} />
          <Typography variant="h4" fontWeight="bold" color="white">
            إدارة الفروع
          </Typography>
        </Box>
      </Paper>

      {/* Messages */}
      {successMessage && <Fade in><Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert></Fade>}
      {errorMessage && <Fade in><Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert></Fade>}

      {/* Form */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, pb: 1, borderBottom: '2px solid #1976d2' }}>
          <SaveIcon color="primary" />
          {editingId ? 'تعديل الفرع' : 'إضافة فرع جديد'}
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="اسم الفرع (إنجليزي/عام) *" name="name" value={formData.name} onChange={handleChange} required />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="اسم الفرع (عربي)" name="nameAr" value={formData.nameAr} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField fullWidth label="الكود" name="code" value={formData.code} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField fullWidth label="المدينة" name="city" value={formData.city} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField fullWidth label="الهاتف" name="phone" value={formData.phone} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="العنوان" name="address" value={formData.address} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="البريد الإلكتروني" name="email" value={formData.email} onChange={handleChange} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>الصندوق الافتراضي</InputLabel>
                        <Select name="defaultSafeId" value={formData.defaultSafeId} onChange={handleChange} label="الصندوق الافتراضي">
                            <MenuItem value=""><em>بدون</em></MenuItem>
                            {safes.map(s => <MenuItem key={s.safeId} value={s.safeId}>{s.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>البنك الافتراضي</InputLabel>
                        <Select name="defaultBankId" value={formData.defaultBankId} onChange={handleChange} label="البنك الافتراضي">
                            <MenuItem value=""><em>بدون</em></MenuItem>
                            {banks.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel control={<Switch checked={formData.isActive} onChange={handleChange} name="isActive" color="success" />} label="نشط" />
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={handleCancel}>إلغاء</Button>
                    <Button type="submit" variant="contained" disabled={loadingSubmit} startIcon={<SaveIcon />}>
                        {loadingSubmit ? 'جاري الحفظ...' : (editingId ? 'تحديث' : 'حفظ')}
                    </Button>
                </Grid>
            </Grid>
        </Box>
      </Paper>

      {/* List */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
         <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <StoreIcon color="secondary" /> قائمة الفروع
         </Typography>

         <TableContainer>
            <Table>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell align="center">#</TableCell>
                        <TableCell>الاسم</TableCell>
                        <TableCell>العربي</TableCell>
                        <TableCell>الكود</TableCell>
                        <TableCell>المدينة</TableCell>
                        <TableCell>الصندوق/البنك</TableCell>
                        <TableCell align="center">الحالة</TableCell>
                        <TableCell align="center">إجراءات</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loadingData ? (
                        <TableRow><TableCell colSpan={8} align="center">جاري التحميل...</TableCell></TableRow>
                    ) : branches.length === 0 ? (
                        <TableRow><TableCell colSpan={8} align="center">لا توجد فروع</TableCell></TableRow>
                    ) : (
                        branches.map((b, i) => (
                            <TableRow key={b.id} hover>
                                <TableCell align="center">{i + 1}</TableCell>
                                <TableCell>{b.name}</TableCell>
                                <TableCell>{b.nameAr || '-'}</TableCell>
                                <TableCell>{b.code || '-'}</TableCell>
                                <TableCell>{b.city || '-'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {b.defaultSafeId && <Chip icon={<LocalAtmIcon />} label={safes.find(s=>s.safeId===b.defaultSafeId)?.name || 'Safe'} size="small" variant="outlined" />}
                                        {b.defaultBankId && <Chip icon={<AccountBalanceIcon />} label={banks.find(x=>x.id===b.defaultBankId)?.name || 'Bank'} size="small" variant="outlined" />}
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    <Chip label={b.isActive ? "نشط" : "غير نشط"} color={b.isActive ? "success" : "default"} size="small" />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton color="primary" onClick={() => selectBranchForEdit(b)}><EditIcon /></IconButton>
                                    <IconButton color="error" onClick={() => handleDelete(b.id)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
         </TableContainer>
      </Paper>
    </Container>
  );
}
